"use client";

import { type FC, type PropsWithChildren, useMemo, useRef } from "react";

import { usePathname, useRouter } from "next/navigation";

import { isServerSide } from "@jti/fe-common";
import { type Navigation, NavigationProvider, type NavigationTarget } from "@jti/fe-navigation";

/** Ensures `search`/`hash` values have their leading `?`/`#` prefix, without doubling it. */
const withPrefix = (prefix: "?" | "#", value?: string): string => {
  if (!value) {
    return "";
  }

  return value.startsWith(prefix) ? value : `${prefix}${value}`;
};

/**
 * Builds an href string from a structured {@link NavigationTarget}.
 * Omitted `search` and `hash` are cleared, not preserved.
 */
export const createHref = ({ pathname, search, hash }: NavigationTarget): string =>
  `${pathname}${withPrefix("?", search)}${withPrefix("#", hash)}`;

/** Reads the current search directly from `window.location` (SSR-safe). */
const getCurrentSearch = (): string => (isServerSide() ? "" : window.location.search);

/** Reads the current hash directly from `window.location` (SSR-safe). */
const getCurrentHash = (): string => (isServerSide() ? "" : window.location.hash);

/** Next.js App Router adapter for the {@link Navigation} contract from `@jti/fe-navigation`. */
export const NextNavigationProvider: FC<PropsWithChildren> = ({ children }) => {
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  const router = useRouter();

  pathnameRef.current = pathname;

  const value: Navigation = useMemo(
    () => ({
      getPathname: () => pathnameRef.current,
      getSearch: getCurrentSearch,
      getHash: getCurrentHash,
      createHref,
      push: (target: NavigationTarget) => router.push(createHref(target)),
      replace: (target: NavigationTarget) => router.replace(createHref(target)),
      back: () => router.back(),
    }),
    [router]
  );

  return <NavigationProvider value={value}>{children}</NavigationProvider>;
};
